"use client";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import { FC, useEffect, useState } from "react";
import { Button } from "../../Button";
import { Card, Media } from "@/types/General";
import Image from "next/image";
import Modal from "@/components/Modal";
import { imageLoader } from "@/helpers/imageLoader";
import { IconBlock } from "@/libs/builder/blocks/IconBlock";

const isSvgUrl = (value: string): boolean => {
  const trimmed = value.trim();
  return /\.svg(\?|#|$)/i.test(trimmed);
};

type DownloadCardProps = {
  media?: Media;
} & Extract<Card, { type: "Download" }>;
export const DownloadCard: FC<DownloadCardProps> = (props) => {
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
  const [svgIcon, setSvgIcon] = useState<{
    url: string;
    content: string;
  } | null>(null);
  const openModal = (): void => setIsModalOpened(true);
  const closeModal = (): void => setIsModalOpened(false);
  const mediaImage =
    props.media?.type === "image" ? props.media.image : undefined;
  const resolvedSvgIcon =
    mediaImage && svgIcon?.url === mediaImage ? svgIcon.content : "";

  useEffect(() => {
    if (!mediaImage || !isSvgUrl(mediaImage)) {
      return;
    }

    const fetchSvgIcon = async () => {
      const response = await fetch(mediaImage);
      const content = await response.text();

      setSvgIcon({ url: mediaImage, content });
    };

    void fetchSvgIcon();
  }, [mediaImage]);

  return (
    <div
      data-automation-group="card"
      className="bg-background shadow-card border-t-symbol-orange-400-graphic border-t-solid border-x-symbol-silver-300-graphic border-b-symbol-silver-300-graphic border-b-solid mb-4 flex break-inside-avoid flex-col gap-6 border-x-1 border-t-2 border-b-1 px-3 py-6"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-row gap-4">
          {props.media?.type === "image" &&
            props.media.image &&
            (isSvgUrl(props.media.image) ? (
              <div
                aria-hidden="true"
                className="text-symbol-orange-400-graphic aspect-square size-8 shrink-0 md:size-12 [&_svg]:size-full [&_svg]:fill-[currentColor]"
              >
                {resolvedSvgIcon && <IconBlock icon={resolvedSvgIcon} />}
              </div>
            ) : (
              <Image
                alt={props.title}
                src={props.media.image}
                width={48}
                height={48}
                loader={imageLoader({
                  image: props.media.image,
                  alt: props.title,
                  width: 48,
                  height: 48,
                })}
                className="aspect-square size-8 md:size-12"
              />
            ))}
          <span
            role="heading"
            aria-level={4}
            aria-label={`${props.type}: ${props.title}`}
            className="text-headline sm:text-subhead-s text-subhead-2xs leading-8 font-bold"
          >
            {props.title}
          </span>
        </div>
        <div
          className="text-body-s text-body-primary"
          dangerouslySetInnerHTML={{ __html: props.description }}
        />
      </div>

      <div className="flex flex-col items-center gap-6 sm:items-start md:flex-row md:justify-center">
        {props.link?.url && props.link?.title && (
          <div className="inline-flex w-fit flex-col items-center gap-2">
            <div className="w-fit">
              <Button
                size="s"
                variant="primary"
                className="flex h-fit w-fit"
                href={props.link.url || ""}
                {...getDataAutomationGroup("button")}
                {...getDataAutomationId(props.link.title || "")}
                {...getDataLinkAttributes({
                  dld: props.link.dld || undefined,
                  dlt: props.link.dlt || "Download Card",
                })}
              >
                {props.link.title}
              </Button>
            </div>
            {props.link?.underTitle && (
              <span className="text-body-secondary text-body-xs w-0 min-w-full text-center wrap-break-word">
                {props.link.underTitle}
              </span>
            )}
          </div>
        )}
        {props.secondLink && (
          <>
            <Button
              size="s"
              variant="secondary"
              className="flex h-fit w-fit"
              {...getDataAutomationGroup("button")}
              {...getDataAutomationId(props.secondLink.title || "")}
              {...getDataLinkAttributes({
                dld: props.secondLink.dld || undefined,
                dlt: props.secondLink.dlt || "Download Card",
              })}
              onClick={openModal}
            >
              {props.secondLink.title}
            </Button>
            <Modal open={isModalOpened} onClose={closeModal} variant="minimal">
              {props.modal}
            </Modal>
          </>
        )}
        {!props.secondLink && props.thirdLink && (
          <Button
            size="s"
            variant="tertiary"
            className="flex h-fit w-fit md:py-3"
            href={props.thirdLink.url || ""}
            {...getDataAutomationGroup("button")}
            {...getDataAutomationId(props.thirdLink?.title || "")}
            {...getDataLinkAttributes({
              dld: props.thirdLink.dld || undefined,
              dlt: props.thirdLink.dlt || "Download Card",
            })}
          >
            {props.thirdLink.title}
          </Button>
        )}
      </div>
    </div>
  );
};
